// @ts-ignore
import { ROLES, SUBJECTS_SLUGS } from '../../../constants';
import { LessonMeta, Resource } from '../defs';
import { CONTENT_TYPE } from './content';

export interface MainPageClientData {
    grade: number;
    role: ROLES;
    blocks: MainPageBlockClientData[];
    id: string;
}

export enum WIDGET_TYPE {
    BANNER_CAROUSEL = 'banners',
    ACTUAL_CONTENT = 'actual_content',
    MARKDOWN = 'markdown',
    SUBJECTS = 'subjects',
    CALENDAR_PLAN = 'calendar_plan',
    CHILD_INFO = 'child_info',
    ABOUT_PLUS = 'about_yandex_plus',
    LESSONS_CAROUSELS = 'lessons_carousels',
    COURSE_CONTAINER_CONTENT = 'course_container',
    PUBLIC_LESSON_BANNER = 'public_lesson_banner',
    TEACHER_RESERVE = 'teacher_reserve',
    STORIES = 'stories',
    COURSE_CALENDAR = 'course_calendar',
    COURSE_CONTAINER_NEXT_LESSONS = 'course_container_next_lessons',
    PUBLIC_LESSONS_SHOWCASE = 'public_lessons_showcase',
}

interface MainPageBlockClientDataCommon {
    id: string;
    title: string | null;
    order: number | null;
    slug?: string | null;
}

export interface ActualContentBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.ACTUAL_CONTENT;
    additional_info: ActualContentWidgetAdditionalInfo;
}

export interface BannersBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.BANNER_CAROUSEL;
    additional_info: {
        banners: ShowcaseBannerParams[];
    };
}

export interface MarkdownBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.MARKDOWN;
    additional_info: {
        markdown: {
            id: string;
            color: string;
            background_color: string;
            content: string | null;
            mobile_content: string | null;
        }[];
    };
}

export interface ChildInfoBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.CHILD_INFO;
    additional_info: {
        child_info: {
            show_info_about_plus: boolean;
        }[];
    };
}

export interface SubjectsBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.SUBJECTS;
    additional_info: {
        subjects: SubjectsItemClientData[];
    };
}

export interface CourseContainerBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.COURSE_CONTAINER_CONTENT;
    additional_info: CourseContainerWidgetAdditionalInfo;
}

// Блок для виджета с рекомендациями уроков
// Виджет не подключен в админку и пока не используется
export interface LessonsCarouselsClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.LESSONS_CAROUSELS;
    additional_info: {
        carousels: LessonsCarouselParams[];
    };
}
interface MainPageBlockClientDataOthers extends MainPageBlockClientDataCommon {
    type:
        | WIDGET_TYPE.CALENDAR_PLAN
        | WIDGET_TYPE.ABOUT_PLUS
        | WIDGET_TYPE.PUBLIC_LESSON_BANNER
        | WIDGET_TYPE.TEACHER_RESERVE;
}

export interface StoriesBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.STORIES;
}

export interface CourseCalendarBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.COURSE_CALENDAR;
}

export interface CourseContainerNextLessonsBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.COURSE_CONTAINER_NEXT_LESSONS;
}

export interface PublicLessonsBlockClientData extends MainPageBlockClientDataCommon {
    type: WIDGET_TYPE.PUBLIC_LESSONS_SHOWCASE;
    additional_info: {
        lessons: {
            copy_of_id: number;
            cover: Resource | null;
            description: string;
            full_name: string;
            id: number;
            is_clean_copy: boolean;
            meta: LessonMeta;
            name: string;
            problems_count: 8;
        }[];
        view_settings: {
            block_id?: string;
            description?: string;
            id?: string;
            title?: string;
            extra_card_settings?: {
                img: string;
                description: string;
                button: {
                    text: string;
                    view?: 'ghost' | 'brand' | 'primary' | 'secondary' | 'outline' | 'contrast' | 'media';
                    action: {
                        type: 'click';
                        link: string;
                    };
                };
            };
        };
    };
}

export type MainPageBlockClientData =
    | ActualContentBlockClientData
    | BannersBlockClientData
    | MarkdownBlockClientData
    | ChildInfoBlockClientData
    | StoriesBlockClientData
    | SubjectsBlockClientData
    | CourseContainerBlockClientData
    | LessonsCarouselsClientData
    | MainPageBlockClientDataOthers
    | CourseCalendarBlockClientData
    | CourseContainerNextLessonsBlockClientData
    | PublicLessonsBlockClientData;

export interface CourseContainerWidgetAdditionalInfo {
    course_container_group: CourseContainerClientData[];
}

export interface CourseContainerClientData {
    id: string;
    name: string;
    description?: string;
    grade?: number;
    visual_settings?: {
        image?: string;
        gradient?: [string, string];
    };
    course_container_info: CourseContainerInfo[];
    subject?: SUBJECTS_SLUGS;
    is_published?: boolean;
    order?: number;
}

export interface CourseContainerInfo {
    id?: string;
    slug: string;
    name: string;
    description?: string;
    visual_settings?: {};
    order?: number;
}

export interface SubjectsItemClientData {
    id: string;
    order: number;
    subject_slug: SUBJECTS_SLUGS;
    visual_settings?: {
        imageSrc: string;
        name: string;
    };
}

interface ActualContentWidgetAdditionalInfo {
    subjects: CalendarPlanBlockClientData[];
}

export interface CalendarPlanBlockClientData {
    id: string;
    name: string;
    slug: SUBJECTS_SLUGS;
    visual_settings?: {
        background_img_colors?: [string, string];
    };
    order: number;
    theme?: {
        id: string;
        description: string;
    };
    activities: CalendarPlanActivityClientData[];
}

export interface CalendarPlanActivityClientData {
    content_id: string;
    content_type: CONTENT_TYPE;
    content_additional_info?: ContentAdditionalInfo;
    activity_view_settings?: ActivityViewSettings;
    additional_info?: AdditionalInfo;
}

export type ContentAdditionalInfo = LessonAdditionalInfo | CatalogThemeAdditionalInfo;

interface LessonViewSettings {
    image: string;
    background: string;
    gradient_colors?: {
        colors?: string[];
    };
}

export interface LessonAdditionalInfo {
    name: string;
    tags: string[];
    lesson_view_settings?: LessonViewSettings;
    is_repeatable?: boolean;
}

interface CatalogThemeAdditionalInfo {
    name: string;
}

export interface AdditionalInfo {
    name?: string;
    description?: string;
    url: string;
    is_open_in_new_tab: boolean;
}

export interface ActivityViewSettings {
    tag_slug: string;
    icon: string;
    name: string;
    description: string | null;
    visual_settings?: {
        bg_color?: string;
        button_text?: string;
    };
}

// Параметры баннера в крутилке на странице Учителя/Родителя/Ученика
export interface ShowcaseBannerParams {
    id: string;
    title: string;
    text: string;
    tag: string;
    button_text?: string;
    button_link: string;
    desktop_image: string;
    mobile_image: string;
    visual_settings: BannerVisualSettings | null;
    order?: number;
}

interface BannerVisualSettings {
    background_color?: string;
    title_color?: string;
    description_color?: string;
}

export interface LessonsCarouselParams {
    title: string;
    titleLink?: string;
    items: LessonsCarouselItem[];
}

// Урок в карусели рекомендаций
export interface LessonsCarouselItem {
    id: number;
    badgeNumber?: number;
    assignedCount?: number;
    rating?: number;

    // subject, description, image (может и остальное) - получаем с какой-то ручки,
    // передавая в нее список всех id проблемов из карусели
    subject?: string;
    description?: string;
    image?: string;
}
